import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Tag,
  ShieldCheck,
  Loader2,
  Briefcase,
  UserCheck,
  User,
  Layers,
  Clock,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { employeeService } from "@/services/employeeService";
import { Employee, Skill } from "@/types/employee";
import { SkillsManager } from "@/components/employees/SkillsManager";
import { EmployeeModal } from "@/components/employees/EmployeeModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDebounce } from "@/hooks/useDebounce";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RolesManagement } from "@/components/employees/RolesManagement";
import { UsersManagement } from "@/components/employees/UsersManagement";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TablePageSkeleton } from "@/components/shared/TablePageSkeleton";

export default function Employees() {
  const { company, user } = useAuth();
  const { toast } = useToast();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [selectedSkillId, setSelectedSkillId] = useState<string>("all");

  const [isSkillManagerOpen, setIsSkillManagerOpen] = useState(false);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(
    null,
  );

  const slug = company?.slug || "";

  // Cargar skills para el filtro
  useEffect(() => {
    const fetchSkills = async () => {
      if (!slug) return;
      try {
        const res = await employeeService.getSkills(slug);
        setSkills(res.data.data || []);
      } catch (error) {
        console.error("Error cargando skills", error);
      }
    };
    fetchSkills();
  }, [slug]);

  const fetchEmployees = async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const params: any = { search: debouncedSearch };
      if (selectedSkillId && selectedSkillId !== "all") {
        params.skill_id = selectedSkillId;
      }
      const response = await employeeService.getEmployees(slug, params);
      setEmployees(response.data.data || []);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los empleados",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [slug, debouncedSearch, selectedSkillId]);

  const stats = {
    total: employees.length,
    skills: new Set(employees.flatMap((e) => e.skills || []).map((s) => s.id))
      .size,
    withUser: employees.filter((e) => e.user).length,
    areas: new Set(employees.map((e) => e.area).filter(Boolean)).size,
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setIsEmployeeModalOpen(true);
  };

  const handleDeleteClick = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!employeeToDelete || !user?.id) return;
    try {
      await employeeService.deleteEmployee(employeeToDelete.id, slug, user.id);
      toast({ title: "Empleado desactivado correctamente" });
      fetchEmployees();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Gestión de Personal
          </h1>
          <p className="text-slate-600">
            Administra los miembros de tu organización, sus habilidades y accesos.
          </p>
        </div>
      </div>

      <TooltipProvider>
        <Tabs defaultValue="employees" className="space-y-6">
          <TabsList className="bg-slate-100 p-1 border border-slate-200/60 w-fit rounded-xl gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="employees" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <Users className="h-4 w-4" />
                  Personal
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Listado de personas que trabajan en la empresa</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="roles" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Permisos de Acceso
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Configura qué puede ver y hacer cada puesto</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger 
                  value="users" 
                  className="gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm transition-all"
                >
                  <UserCheck className="h-4 w-4" />
                  Historial de Cuentas
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>Consulta las cuentas de acceso creadas (Solo lectura)</p>
              </TooltipContent>
            </Tooltip>
          </TabsList>

        <TabsContent value="employees" className="space-y-6">
          <div className="flex justify-end gap-3 -mt-14 mb-4">
            <Button
              variant="outline"
              onClick={() => setIsSkillManagerOpen(true)}
              className="gap-2"
            >
              <Tag className="h-4 w-4 text-slate-500" />
              Gestionar Skills
            </Button>
            <Button
              onClick={() => {
                setEditingEmployee(null);
                setIsEmployeeModalOpen(true);
              }}
              className="gap-2 bg-black hover:bg-gray-800 text-white border-0 shadow-lg shadow-gray-900/20"
            >
              <Plus className="h-4 w-4" />
              Añadir Personal
            </Button>
          </div>

          {/* Tarjetas de Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-slate-200/60 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Miembros</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {stats.total}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500/20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Skills</p>
                    <p className="text-2xl font-bold text-green-600">
                      {stats.skills}
                    </p>
                  </div>
                  <Tag className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Con Usuario</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {stats.withUser}
                    </p>
                  </div>
                  <ShieldCheck className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Áreas</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {stats.areas}
                    </p>
                  </div>
                  <Layers className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Barra de Búsqueda */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, puesto o ID..."
                    className="pl-9"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <Select value={selectedSkillId} onValueChange={setSelectedSkillId}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Filtrar por Skill" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las Skills</SelectItem>
                    {skills.map((skill) => (
                      <SelectItem key={skill.id} value={skill.id}>
                        {skill.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Tabla de Empleados */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <TablePageSkeleton hasHeader={false} hasMetrics={false} columnCount={5} rowCount={8} />
              ) : employees.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>No se encontraron empleados registrados.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="w-[300px]">Empleado</TableHead>
                      <TableHead>Puesto & Área</TableHead>
                      <TableHead>Turno</TableHead>
                      <TableHead>Habilidades</TableHead>
                      <TableHead>Acceso Usuario</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow
                        key={emp.id}
                        className="group hover:bg-slate-50/50 transition-colors"
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 border border-slate-200 bg-white">
                              <AvatarImage src={emp.user?.avatar_url} />
                              <AvatarFallback className="bg-slate-100 text-slate-400">
                                <User className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-slate-900">
                                {emp.name}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1.5 h-5 font-normal text-slate-500 border-slate-200 bg-slate-50/50"
                                >
                                  {emp.identification}
                                </Badge>
                                {!emp.is_active && (
                                  <Badge
                                    variant="destructive"
                                    className="text-[10px] h-5 px-1.5"
                                  >
                                    Inactivo
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-slate-700">
                              {emp.position}
                            </p>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Briefcase className="h-3 w-3" /> {emp.area}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {emp.workShift ? (
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-slate-700">{emp.workShift.name}</span>
                              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {emp.workShift.start_time.substring(0, 5)} - {emp.workShift.end_time.substring(0, 5)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs italic text-center">Sin turno</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1.5 max-w-[250px]">
                            {emp.skills && emp.skills.length > 0 ? (
                              emp.skills.slice(0, 3).map((skill) => (
                                <Badge
                                  key={skill.id}
                                  variant="secondary"
                                  className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-transparent text-[10px] px-2"
                                >
                                  {skill.name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-slate-400 text-xs italic">
                                -
                              </span>
                            )}
                            {emp.skills && emp.skills.length > 3 && (
                              <Badge
                                variant="outline"
                                className="text-[10px] text-slate-500"
                              >
                                +{emp.skills.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {emp.user ? (
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-2 py-1 rounded-md w-fit border border-green-100">
                              <ShieldCheck className="h-3.5 w-3.5" />
                              <span className="font-medium truncate max-w-[150px]">
                                {emp.user.email}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400 text-xs flex items-center gap-1.5 px-2 py-1">
                                <UserCheck className="h-3.5 w-3.5 opacity-50" /> Sin vincular
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-100"
                                onClick={() => handleEdit(emp)}
                              >
                                + Conectar Acceso
                              </Button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                              onClick={() => handleEdit(emp)}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteClick(emp)}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <RolesManagement />
        </TabsContent>

        <TabsContent value="users">
          <UsersManagement 
            slug={slug} 
            onLinkEmployee={(user) => {
              setEditingEmployee({ 
                id: "", 
                name: user.first_name ? `${user.first_name} ${user.last_name || ""}` : (user.name || ""),
                position: "",
                area: "",
                identification: "",
                is_active: true,
                skills: [],
                user: user,
                user_id: user.id
              });
              setIsEmployeeModalOpen(true);
            }} 
          />
        </TabsContent>
      </Tabs>

      <SkillsManager
        isOpen={isSkillManagerOpen}
        onClose={() => setIsSkillManagerOpen(false)}
        slug={slug}
      />

      <EmployeeModal
        isOpen={isEmployeeModalOpen}
        onClose={() => setIsEmployeeModalOpen(false)}
        onSave={fetchEmployees}
        employee={editingEmployee}
        slug={slug}
        companyId={company?.id || ""}
        currentUserId={user?.id || ""}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar empleado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción marcará a <strong>{employeeToDelete?.name}</strong>{" "}
              como inactivo. No podrá acceder al sistema, pero sus datos
              históricos se conservarán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 border-red-600"
            >
              Sí, desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </TooltipProvider>
    </div>
  );
}
